import { ReactNode } from 'react';
import { cn } from '@shared/util/classnames';
import { useBoolean } from '@shared/util/hooks/useBoolean';
import { useCallbackOnEnter } from '@shared/util/hooks/useCallbackOnEnter';
import { Button, ButtonColor } from '@ui/Button';
import { AlertBody, AlertFooter, AlertHeader } from './Alert';
import { Modal, ModalContent, ModalProps } from './Modal';

export interface ConfirmProps extends Omit<ModalProps, 'title' | 'children'> {
  title?: ReactNode;
  message?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  confirmColor?: ButtonColor;
  onConfirm?: () => void | Promise<void>;
  cancelLabel?: string;
  cancelColor?: ButtonColor;
  onCancel?: () => void | Promise<void>;
  modal?: boolean;
  className?: string;
  confirmOnEnter?: boolean;
}

export function Confirm({ modal = true, ...props }: ConfirmProps) {
  if (modal) {
    return <ModalConfirm {...props} />;
  } else {
    return <ConfirmContent {...props} />;
  }
}

function ModalConfirm({
  title,
  message,
  children,
  confirmLabel,
  confirmColor,
  onConfirm,
  cancelLabel,
  cancelColor,
  onCancel,
  size = 'xs',
  confirmOnEnter,
  ...modalProps
}: ConfirmProps) {
  return (
    <Modal {...modalProps} variant="alert" size={size}>
      <ModalContent className="p-6">
        {onClose => (
          <ConfirmContent
            {...{
              title,
              message,
              children,
              confirmLabel,
              confirmColor,
              onConfirm,
              cancelLabel,
              cancelColor,
              onCancel,
              onClose,
              confirmOnEnter,
            }}
          />
        )}
      </ModalContent>
    </Modal>
  );
}

function ConfirmContent({
  title,
  message,
  children = message,
  confirmLabel = 'Confirm',
  confirmColor = 'danger',
  onConfirm,
  cancelLabel = 'Cancel',
  cancelColor = 'secondary',
  onCancel,
  onClose,
  className,
  confirmOnEnter = true,
}: ConfirmProps) {
  const [loading, startLoading, stopLoading] = useBoolean();

  const handleConfirm = async () => {
    startLoading();
    await onConfirm?.();
    void onClose?.();
    stopLoading();
  };

  useCallbackOnEnter({ callback: handleConfirm, enabled: confirmOnEnter });

  return (
    <div className={cn('flex flex-col gap-3 max-w-[18rem] w-fit', className)}>
      {title && <AlertHeader>{title}</AlertHeader>}
      {children && <AlertBody>{children}</AlertBody>}
      <AlertFooter>
        <Button
          color={cancelColor}
          onPress={async () => {
            await onCancel?.();
            void onClose?.();
          }}
          isDisabled={loading}
        >
          {cancelLabel}
        </Button>
        <Button color={confirmColor} onPress={handleConfirm} isLoading={loading}>
          {confirmLabel}
        </Button>
      </AlertFooter>
    </div>
  );
}
